import LoadFailure from '../Brand/LoadFailure';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useInRouterContext } from 'react-router-dom';
import { CloseIcon, ShareIcon } from '../Brand/Icon';
import ProviderLogo from './ProviderLogo';
import useLinkPreview from './useLinkPreview';
import useSinglePlayer, { isInteractiveCard } from './playerState';
import { getOfficialEmbed, isMediaLink } from './embeds';
import MediaViewportContext, { revealPlayer } from './MediaViewportContext';
import { compactLinkLabel, isInstagramUrl, validLinkLabel } from '../../utils/externalLinkText';
import { internalContentTarget } from '../../appLinks/links';
import './index.scss';

const stop = event => event.stopPropagation();
const PlayIcon = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="m8 5 11 7-11 7Z" /></svg>;

export default function ExternalLink({ href, label, customLabel, editing = false, onEdit, onBlocked }) {
  const { t } = useTranslation();
  const inRouter = useInRouterContext();
  const { link, retry } = useLinkPreview(href);
  const { active, open, close } = useSinglePlayer();
  const prepareViewport = useContext(MediaViewportContext);
  const rootRef = useRef(null);
  const openRef = useRef(null);
  const playerRef = useRef(null);
  const blockedRef = useRef(null);
  const [width, setWidth] = useState(null);
  const embed = useMemo(() => getOfficialEmbed(link), [link]);
  const mediaLink = Boolean(embed) || isMediaLink(href) || isMediaLink(link.url);
  const internalTarget = internalContentTarget(href);
  const blocked = link.status === 'blocked';
  const pending = link.status === 'pending';
  const safe = !blocked && (link.status === 'ready' || (link.status === 'unavailable' && link.safe));
  // Failed/blocked API metadata may contain a generic server-language caption.
  // Preserve the user's label; the status below is localized in every state.
  const title = validLinkLabel(customLabel) || compactLinkLabel(href, (safe && link.title) || label || href);
  const text = (key, fallback) => t(`brand:${key}`, { defaultValue: fallback });

  useEffect(() => {
    if (blocked && blockedRef.current !== href) {
      blockedRef.current = href;
      onBlocked?.({ url: href, reason: link.reason });
    }
  }, [blocked, href, link.reason, onBlocked]);

  useEffect(() => {
    if (!embed) return undefined;
    const element = rootRef.current;
    // Inline NodeView wrappers describe the card's current width, not the
    // space available for its expanded player. Tiptap can mount React while
    // the wrapper is still detached, so resolve the block on every measure.
    let observedParent;
    let observer;
    let frame;
    const measure = () => {
      if (!element?.isConnected) return;
      let parent = element.parentElement;
      while (parent) {
        const display = getComputedStyle(parent).display;
        if (!display.startsWith('inline') && display !== 'contents') break;
        parent = parent.parentElement;
      }
      if (!parent) return;
      if (parent !== observedParent) {
        if (observedParent) observer?.unobserve(observedParent);
        observer?.observe(parent);
        observedParent = parent;
      }
      const parentStyle = getComputedStyle(parent);
      const ownStyle = getComputedStyle(element);
      const horizontal = style => ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
        .reduce((sum, property) => sum + (parseFloat(style[property]) || 0), 0);
      const available = parent.getBoundingClientRect().width - horizontal(parentStyle) - horizontal(ownStyle);
      setWidth(available > 0 ? available : null);
    };
    // Showing the play button changes the compact card's size. Defer another
    // measurement to avoid changing an observed box within ResizeObserver's
    // own delivery cycle (which browsers report as an undelivered loop).
    observer = window.ResizeObserver ? new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    }) : null;
    // Observing the card also catches a detached NodeView being attached.
    if (element) observer?.observe(element);
    measure();
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); };
  }, [embed]);

  useEffect(() => { if (!safe || !embed) close(); }, [safe, embed, close]);

  useEffect(() => {
    if (!active) return undefined;
    const check = () => { if (!isInteractiveCard(rootRef.current)) close(); };
    check();
    document.addEventListener('visibilitychange', check);
    const observer = new MutationObserver(check);
    let ancestor = rootRef.current?.parentElement;
    while (ancestor && ancestor !== document.body) {
      observer.observe(ancestor, { attributes: true, attributeFilter: ['class', 'aria-hidden', 'inert', 'hidden', 'style'] });
      ancestor = ancestor.parentElement;
    }
    const intersection = window.IntersectionObserver && new IntersectionObserver(entries => {
      if (entries.some(entry => !entry.isIntersecting)) close();
    });
    if (intersection && playerRef.current) intersection.observe(playerRef.current);
    return () => {
      document.removeEventListener('visibilitychange', check);
      observer.disconnect();
      intersection?.disconnect();
    };
  }, [active, close]);

  const tooSmall = embed?.minWidth && width && width < embed.minWidth;
  useEffect(() => { if (tooSmall) close(); }, [tooSmall, close]);
  const canPlay = safe && embed && !tooSmall;
  const playing = active && canPlay;
  useLayoutEffect(() => {
    if (playing) revealPlayer(playerRef.current?.parentElement);
  }, [playing]);
  const originalProps = { href: link.url, target: '_blank', rel: 'noopener noreferrer' };
  const content = <>
    {canPlay && <span className="external-link__logo"><ProviderLogo provider={link.provider} /></span>}
    <span className="external-link__caption"><span className="external-link__title">{title}</span>
      {canPlay && link.author && <span className="external-link__author">{link.author}</span>}
    </span>
    {canPlay && <PlayIcon />}
  </>;

  const editLabel = `${text('linkEditText', 'Edit link text')}: ${title}`;
  return <span ref={rootRef} className={`external-link${canPlay ? '' : ' external-link--text'}${isInstagramUrl(href) ? ' external-link--one-line' : ''}${playing ? ' external-link--playing' : ''}`}
    data-provider={link.provider} data-state={link.status} data-editing={editing || undefined} data-no-swipe
    onClick={stop} onDoubleClick={stop} onPointerDown={stop} onTouchStart={stop} onTouchEnd={stop}>
    <span className="external-link__row">
      {canPlay ? <button ref={openRef} type="button" className="external-link__primary" aria-label={`${text('linkPlay', 'Open player')}: ${title}`}
          onClick={() => {
            if (!isInteractiveCard(rootRef.current)) return;
            // Safari does not always focus tapped buttons. Explicitly leave
            // the editable surface so keyboard/caret scrolling cannot pull
            // the newly opened player out of view when its iframe loads.
            openRef.current?.focus({ preventScroll: true });
            prepareViewport?.();
            open();
          }}>{content}</button>
          : editing && onEdit && !mediaLink ? <button type="button" className="external-link__primary" aria-label={editLabel} onClick={onEdit}>{content}</button>
          : editing || !safe ? <span className="external-link__primary">{content}</span>
            : internalTarget ? (inRouter ? <Link className="external-link__primary" to={internalTarget}>{content}</Link>
              : <a className="external-link__primary" href={internalTarget}>{content}</a>)
              : <a className="external-link__primary" {...originalProps}>{content}</a>}
    </span>
    {pending && <span className="external-link__status" role="status">{text('linkLoading', 'Checking link…')}</span>}
    {blocked && <span className="external-link__status" role="status">{text('linkBlocked', 'This link cannot be shared.')}</span>}
    {link.status === 'unavailable' && !safe && <LoadFailure onRetry={retry} />}
    {tooSmall && !editing && <span className="external-link__status">{text('linkPlayerTooSmall', 'Open the original to play on this screen.')}</span>}
    {playing && <span className={`external-link__player${embed.video ? ' external-link__player--video' : ''}`}>
      <span className="external-link__player-controls">
        <a {...originalProps} aria-label={text('linkOriginal', 'Open original')}><ShareIcon size={22} /></a>
        <button type="button" aria-label={text('linkClose', 'Close player')} onClick={() => { close(); openRef.current?.focus({ preventScroll: true }); }}><CloseIcon size={22} /></button>
      </span>
      <iframe ref={playerRef} src={embed.src} title={`${text('linkPlayerTitle', 'Media player')}: ${title}`} height={embed.height} width="100%"
        allow="encrypted-media; fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
    </span>}
  </span>;
}
